// src/components/rpa/agent/AgentGovernanceDashboard.js
//
// System-admin-only view of the RPA Agent's Token Cost Governance controls
// (WISP §7.2, §12.3 / AI AUP §5.5.4): daily platform budget with soft/hard
// thresholds, per-session spend against the $10 session cap, the current
// guardrail configuration, and a live feed of guardrail activations.
//
// Access control: the backend route (/admin/rpa/agent/governance/metrics)
// is gated by requireAdmin + requireSystemAdmin, so a non-system-admin gets
// a 403 and this component shows an access-denied state rather than any
// data. This component does not additionally gate on the client's own
// notion of systemRole — wire that into the route/nav-link that renders
// this component once systemRole is exposed in the client auth state.
//
// Live updates: listens for the 'agent_governance_update' socket event
// (emitted platform-wide by global.broadcastGovernanceUpdate in server.js)
// in addition to polling every 20s as a fallback.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import io from 'socket.io-client';
import './AgentGovernanceDashboard.css';

const API_URL = process.env.REACT_APP_API || 'http://localhost:8000';
// ⭐ Socket.IO's io(url) argument is more than just a host — a trailing path
// segment (like the /api this app's REACT_APP_API already includes, per the
// convention every other component here follows) is interpreted as a
// namespace, not stripped like a normal HTTP base URL would be. The actual
// transport path is controlled separately by the `path: '/socket.io'`
// option below, so the connection URL itself needs to be the bare origin.
const SOCKET_URL = API_URL.replace(/\/api\/?$/, '');

const fmtMoney = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtTime  = (d) => d ? new Date(d).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
const fmtMs    = (ms) => {
  const mins = Math.round((ms || 0) / 60000);
  return mins >= 1 ? `${mins} min` : `${Math.round((ms || 0) / 1000)} sec`;
};

const GUARDRAIL_LABELS = {
  step_budget:             'Step Budget Reached',
  session_token_cap:       'Session Token Cap Reached',
  similarity_breaker:      'Similarity Breaker (Loop Detected)',
  wall_clock_timeout:      'Wall-Clock Timeout',
  daily_soft_alert_50:     'Daily Budget — 50% Alert',
  daily_soft_alert_80:     'Daily Budget — 80% Alert',
  daily_hard_kill:         'Daily Budget — HARD KILL',
  daily_hard_kill_resumed: 'Daily Budget — Resumed by Admin',
};

const GUARDRAIL_SEVERITY = {
  step_budget:             'warn',
  session_token_cap:       'warn',
  similarity_breaker:      'warn',
  wall_clock_timeout:      'warn',
  daily_soft_alert_50:     'info',
  daily_soft_alert_80:     'warn',
  daily_hard_kill:         'critical',
  daily_hard_kill_resumed: 'ok',
};

const AgentGovernanceDashboard = () => {
  const [snapshot, setSnapshot]   = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [resuming, setResuming]   = useState(false);
  const [simulating, setSimulating] = useState(null); // which demo button is in-flight
  const socketRef = useRef(null);

  const fetchSnapshot = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/rpa/agent/governance/metrics`, {
        withCredentials: true,
      });
      setSnapshot(res.data);
      setError(null);
      setForbidden(false);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        setForbidden(true);
      } else {
        setError(err?.response?.data?.error || 'Failed to load governance metrics.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSnapshot();
    const interval = setInterval(fetchSnapshot, 20000);

    // Live updates — falls back gracefully to polling if the socket doesn't
    // connect for any reason (auth cookie missing, network policy, etc.).
    try {
      const socket = io(SOCKET_URL, { path: '/socket.io', withCredentials: true, transports: ['websocket', 'polling'] });
      socketRef.current = socket;
      socket.on('agent_governance_update', () => {
        fetchSnapshot();
      });
    } catch (e) {
      console.warn('[AgentGovernanceDashboard] socket connection failed, relying on polling:', e?.message);
    }

    return () => {
      clearInterval(interval);
      socketRef.current?.disconnect();
    };
  }, [fetchSnapshot]);

  const handleResume = async () => {
    if (!window.confirm('Authorize resumption of the AI agent for the rest of today? This lifts the daily budget hard kill.')) {
      return;
    }
    setResuming(true);
    try {
      const res = await axios.post(`${API_URL}/admin/rpa/agent/governance/resume`, {}, { withCredentials: true });
      setSnapshot(res.data);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to authorize resumption.');
    } finally {
      setResuming(false);
    }
  };

  // ⭐ Demo/testing tool — fires real events through the same functions
  // actual AI spend and guardrail activations use, so this produces a
  // genuine live update (socket broadcast, AuditEvent log, UI react) rather
  // than a mocked-up UI state.
  const handleSimulate = async (payload, key) => {
    // ⭐ Unlike 'spend' and 'guardrail', 'hard_kill' isn't cosmetic — it
    // flips the SAME platform-wide switch a real budget exhaustion would,
    // which blocks every real AI-calling request app-wide (agent chat,
    // financial-audit steps, SOC testing, everything) until someone
    // authorizes resumption. On a live production server, that means real
    // client work in progress can fail while this is active. Confirm
    // before firing it — the other two demo buttons don't touch anything
    // real and don't need this.
    if (key === 'hard_kill') {
      const confirmed = window.confirm(
        'This simulates a REAL daily budget hard kill — it will block every AI-calling request ' +
        'platform-wide (agent chat, financial audits, SOC testing, everything) for ALL users until ' +
        'someone clicks "Authorize Resumption". No real dollars are spent, but real in-progress work ' +
        'can fail while this is active. Continue?'
      );
      if (!confirmed) return;
    }

    setSimulating(key);
    try {
      const res = await axios.post(`${API_URL}/admin/rpa/agent/governance/simulate`, payload, { withCredentials: true });
      setSnapshot(res.data);
    } catch (err) {
      setError(err?.response?.data?.error || 'Simulation failed.');
    } finally {
      setSimulating(null);
    }
  };

  const handleResetToday = async () => {
    const confirmed = window.confirm(
      'This fully resets TODAY\'S budget — spend, alerts, and hard-kill state all clear back to zero. ' +
      'This clears REAL spend recorded today too, not just simulated spend (there\'s no way to tell them ' +
      'apart). Only use this for testing. Continue?'
    );
    if (!confirmed) return;

    setSimulating('reset');
    try {
      const res = await axios.post(`${API_URL}/admin/rpa/agent/governance/reset-today`, {}, { withCredentials: true });
      setSnapshot(res.data);
    } catch (err) {
      setError(err?.response?.data?.error || 'Reset failed.');
    } finally {
      setSimulating(null);
    }
  };

  if (forbidden) {
    return (
      <div className="gov-dashboard gov-dashboard--denied">
        <div className="gov-denied-card">
          <div className="gov-denied-icon">🔒</div>
          <h2>System Administrator Access Required</h2>
          <p>The Token Cost Governance dashboard is restricted to system administrators.</p>
        </div>
      </div>
    );
  }

  if (loading && !snapshot) {
    return <div className="gov-dashboard gov-dashboard--loading">Loading governance metrics…</div>;
  }

  if (error && !snapshot) {
    return <div className="gov-dashboard gov-dashboard--error">{error}</div>;
  }

  const s = snapshot || {};
  const pct = Math.min(100, s.dailyPercent || 0);
  const barClass = s.hardKillActive ? 'critical' : pct >= 80 ? 'warn' : pct >= 50 ? 'caution' : 'ok';

  return (
    <div className="gov-dashboard">
      <div className="gov-header">
        <div>
          <h1>AI Agent Token Cost Governance</h1>
          <p className="gov-subtitle">WISP §7.2 / AI AUP §5.5.4 — daily budget, session caps, and guardrail activity</p>
        </div>
        <div className="gov-date">{s.date}</div>
      </div>

      <div className="gov-demo-panel">
        <span className="gov-demo-label">Demo tools</span>
        <button
          className="gov-demo-btn"
          disabled={!!simulating}
          onClick={() => handleSimulate({ type: 'spend', amountUSD: 5 }, 'spend')}
        >
          {simulating === 'spend' ? 'Sending…' : 'Simulate $5 Spend'}
        </button>
        <button
          className="gov-demo-btn"
          disabled={!!simulating}
          onClick={() => handleSimulate({ type: 'guardrail', guardrailType: 'similarity_breaker' }, 'guardrail')}
        >
          {simulating === 'guardrail' ? 'Sending…' : 'Simulate Guardrail Activation'}
        </button>
        <button
          className="gov-demo-btn gov-demo-btn--danger"
          disabled={!!simulating}
          onClick={() => handleSimulate({ type: 'hard_kill' }, 'hard_kill')}
        >
          {simulating === 'hard_kill' ? 'Sending…' : 'Simulate Daily Hard Kill'}
        </button>
        <span className="gov-demo-divider" />
        <button
          className="gov-demo-btn gov-demo-btn--danger"
          disabled={!!simulating}
          onClick={handleResetToday}
          title="Testing only — wipes real spend recorded today too"
        >
          {simulating === 'reset' ? 'Resetting…' : 'Reset Today\'s Budget (Testing)'}
        </button>
      </div>

      {s.hardKillActive && (
        <div className="gov-killbanner">
          <div>
            <strong>Agent execution suspended platform-wide.</strong>
            <div>Daily AI budget reached at {fmtTime(s.hardKillTriggeredAt)}. No agent step, chain run, PBC letter, or management report will execute until resumed.</div>
          </div>
          <button className="gov-resume-btn" onClick={handleResume} disabled={resuming}>
            {resuming ? 'Authorizing…' : 'Authorize Resumption'}
          </button>
        </div>
      )}

      <div className="gov-grid">
        <div className="gov-card gov-card--budget">
          <div className="gov-card-title">Daily Platform Budget</div>
          <div className="gov-budget-numbers">
            <span className="gov-budget-spent">{fmtMoney(s.dailySpendUSD)}</span>
            <span className="gov-budget-of"> of {fmtMoney(s.dailyBudgetUSD)}</span>
          </div>
          <div className="gov-progress-track">
            <div className={`gov-progress-fill gov-progress-fill--${barClass}`} style={{ width: `${pct}%` }} />
            <div className="gov-progress-marker" style={{ left: '50%' }} />
            <div className="gov-progress-marker" style={{ left: '80%' }} />
          </div>
          <div className="gov-progress-legend">
            <span>0%</span><span>50% alert</span><span>80% alert</span><span>100% hard kill</span>
          </div>
          <div className="gov-budget-meta">
            {s.alert50TriggeredAt && <span>50% alert fired {fmtTime(s.alert50TriggeredAt)}</span>}
            {s.alert80TriggeredAt && <span>80% alert fired {fmtTime(s.alert80TriggeredAt)}</span>}
            {s.manuallyResumed && s.resumedAt && <span>Resumed by admin {fmtTime(s.resumedAt)}</span>}
          </div>
        </div>

        <div className="gov-card gov-card--config">
          <div className="gov-card-title">Active Guardrail Configuration</div>
          <div className="gov-config-row"><span>Step Budget</span><span>{s.stepBudgetMax} steps / chain</span></div>
          <div className="gov-config-row"><span>Session Token Cap</span><span>{fmtMoney(s.sessionTokenCapUSD)} / session</span></div>
          <div className="gov-config-row"><span>Wall-Clock Timeout</span><span>{fmtMs(s.wallClockTimeoutMs)} / chain</span></div>
          <div className="gov-config-row"><span>Similarity Breaker</span><span>{s.similarityBreakerN} repeats</span></div>
          <div className="gov-config-note">Set via env vars — see AGENT_* config in agentGuardrails.js</div>
        </div>
      </div>

      <div className="gov-card">
        <div className="gov-card-title">Spend By API Route Today ({(s.spendByRoute || []).length})</div>
        <p className="gov-card-hint">Every metered Azure OpenAI call, attributed to the specific endpoint that made it — the agent chat, a direct financial-audit step, CMMC question generation, HUD analysis, or anything else that calls Azure.</p>
        {(!s.spendByRoute || s.spendByRoute.length === 0) ? (
          <div className="gov-empty">No AI spend recorded today.</div>
        ) : (
          <table className="gov-table">
            <thead>
              <tr><th>Route</th><th>Calls</th><th>Spend</th><th>Last Call</th></tr>
            </thead>
            <tbody>
              {s.spendByRoute.map((r) => (
                <tr key={r.route}>
                  <td className="gov-mono gov-route-cell">{r.route}</td>
                  <td>{r.calls}</td>
                  <td>{fmtMoney(r.costUSD)}</td>
                  <td>{fmtTime(r.lastCall)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="gov-card">
        <div className="gov-card-title">Active Sessions Today ({(s.activeSessions || []).length})</div>
        {(!s.activeSessions || s.activeSessions.length === 0) ? (
          <div className="gov-empty">No agent sessions recorded today.</div>
        ) : (
          <table className="gov-table">
            <thead>
              <tr>
                <th>Session</th><th>User</th><th>Doc</th><th>Steps</th><th>Spend</th><th>Last Activity</th><th></th>
              </tr>
            </thead>
            <tbody>
              {s.activeSessions.map((sess) => (
                <tr key={sess.sessionId || Math.random()}>
                  <td className="gov-mono">{(sess.sessionId || '—').slice(0, 10)}</td>
                  <td className="gov-mono">{(sess.userId || '—').slice(0, 10)}</td>
                  <td className="gov-mono">{(sess.docId || '—').slice(0, 10)}</td>
                  <td>{sess.steps}</td>
                  <td className={sess.overSessionCap ? 'gov-over-cap' : ''}>{fmtMoney(sess.costUSD)}</td>
                  <td>{fmtTime(sess.lastActivity)}</td>
                  <td>
                    {sess.overSessionCap && <span className="gov-badge gov-badge--warn">Over cap</span>}
                    {sess.hadGuardrailEvent && <span className="gov-badge gov-badge--info">Guardrail fired</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="gov-card">
        <div className="gov-card-title">Recent Guardrail Activations</div>
        {(!s.recentGuardrailEvents || s.recentGuardrailEvents.length === 0) ? (
          <div className="gov-empty">No guardrail activations recorded.</div>
        ) : (
          <ul className="gov-event-list">
            {s.recentGuardrailEvents.map((ev) => {
              const type = ev.guardrailEvent?.type;
              const sev  = GUARDRAIL_SEVERITY[type] || 'info';
              return (
                <li key={ev._id} className={`gov-event gov-event--${sev}`}>
                  <span className="gov-event-label">{GUARDRAIL_LABELS[type] || type}</span>
                  <span className="gov-event-detail">{ev.guardrailEvent?.detail}</span>
                  <span className="gov-event-time">{fmtTime(ev.guardrailEvent?.triggeredAt || ev.createdAt)}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AgentGovernanceDashboard;